import chainHandlers from "@gef-ui/utils/chainHandlers";
import PropTypes from 'prop-types';
import any from "ramda/es/any";
import compose from "ramda/es/compose";
import equals from "ramda/es/equals";
import not from "ramda/es/not";
import o from "ramda/es/o";
import omit from "ramda/es/omit";
import pluck from "ramda/es/pluck";
import prop from "ramda/es/prop";
import when from "ramda/es/when";
import cx from "ramda-extension/es/cx";
import isNilOrEmptyString from "ramda-extension/es/isNilOrEmptyString";
import isTruthy from "ramda-extension/es/isTruthy";
import noop from "ramda-extension/es/noop";
import notEqual from "ramda-extension/es/notEqual";
import React, { Component, createRef } from 'react';
import TagsInputExternal from 'react-tagsinput';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import gefWhen from '../../../utils/when';
import Pill from '../../Pill';
import textFieldComposer from '../../TextField/TextFieldComposer';
import { cursorPositions } from '../constants';
const COMPONENT_NAME = 'TagsInput';
const blurFocusProps = [
    'onBlur',
    'onFocus'
];
const omitControlProps = omit([
    'value',
    ...blurFocusProps
]);
const omitInvalidProps = omit([
    'className',
    'addTag',
    ...blurFocusProps
]);
const shouldShrink = any(isTruthy);
const hasInputValue = compose(not, isNilOrEmptyString, prop('value'));
const equalsStart = equals(cursorPositions.START);
class TagsInput extends Component {
    constructor(props){
        super(props);
        this.tagsInputRef = /*#__PURE__*/ createRef();
    }
    componentDidUpdate(prevProps) {
        const { initialCursorPosition, value } = this.props;
        if (equalsStart(initialCursorPosition)) {
            this.handleCursorPositionChange(value, prevProps.value);
        }
    }
    resetInputCursorPosition = ()=>{
        this.tagsInputRef.current.setSelectionRange(0, 0);
    };
    handleCursorPositionChange = when(notEqual, this.resetInputCursorPosition);
    handleInputFocus = ()=>{
        this.tagsInputRef.current.focus();
    };
    defaultRenderInput = ({ onChange, value, ...other })=>{
        const { autoFocus, controlProps, type, inputControlClassName, controlInputClassName } = this.props;
        const onBlurChain = chainHandlers(pluck('onBlur', [
            controlProps,
            other
        ]));
        const onFocusChain = chainHandlers(pluck('onFocus', [
            controlProps,
            other
        ]));
        const otherControlProps = omitControlProps(controlProps);
        const otherInputTagsProps = omitInvalidProps(other);
        return /*#__PURE__*/ React.createElement("input", {
            autoFocus: autoFocus,
            ...otherInputTagsProps,
            ...otherControlProps,
            onFocus: onFocusChain,
            onBlur: onBlurChain,
            onChange: onChange,
            value: value,
            className: cx([
                'Input-control',
                'TagsInput-input'
            ], inputControlClassName, controlInputClassName),
            type: type,
            ref: this.tagsInputRef
        });
    };
    defaultRenderTag = ({ tag, key, disabled, onRemove, getTagDisplayValue })=>/*#__PURE__*/ React.createElement(Pill, {
            onClick: ()=>onRemove(key),
            pillId: key,
            key: key,
            className: cx([
                'Input-pill',
                'TagsInput-tag'
            ]),
            disabled: disabled
        }, getTagDisplayValue(tag));
    defaultRenderLayout = (tagComponents, inputComponent)=>{
        const { asyncValidationInProgress, controlClassName, isFocused, value, defaultInputValue, tagsInputProps, inputProps, controlProps, displayRequiredClassName, required } = this.props;
        const { placeholder } = controlProps;
        const { inputValue } = tagsInputProps;
        const { floatingLabelText, onInputWrapperClick, htmlFor } = inputProps;
        const floatingLabelShrink = shouldShrink([
            placeholder,
            defaultInputValue,
            inputValue,
            isFocused,
            hasInputValue(this.tagsInputRef.current),
            value.length > 0
        ]);
        const isRequired = required || inputProps.required;
        const labelElement = gefWhen(floatingLabelText, /*#__PURE__*/ React.createElement("label", {
            className: cx('Input-labelWrapper', {
                'Input-labelWrapper--shrink': floatingLabelShrink
            }),
            htmlFor: htmlFor
        }, /*#__PURE__*/ React.createElement("span", {
            className: cx('Input-label', {
                'Input-label--required': displayRequiredClassName && isRequired,
                'Input-label--shrink': floatingLabelShrink
            })
        }, floatingLabelText)));
        return /*#__PURE__*/ React.createElement(React.Fragment, null, labelElement, /*#__PURE__*/ React.createElement("div", {
            className: cx('Input-controlWrapper', {
                'Input-controlWrapper--shrink': floatingLabelShrink
            }, controlClassName),
            onClick: chainHandlers([
                onInputWrapperClick,
                this.handleInputFocus
            ])
        }, tagComponents, inputComponent), asyncValidationInProgress && /*#__PURE__*/ React.createElement("div", {
            className: "Input-asyncPreloader Preloader"
        }));
    };
    handleOnChange = (tags)=>{
        const { onChange } = this.props;
        onChange?.(undefined, tags);
    };
    render() {
        const { hasError, readOnly, inputProps, controlProps, className, value, tagsInputProps, defaultInputValue, outline } = this.props;
        const { disabled } = controlProps;
        const enhancedTagsInputProps = {
            inputValue: defaultInputValue,
            ...tagsInputProps
        };
        return /*#__PURE__*/ React.createElement(TagsInputExternal, {
            className: cx('Input', 'Input--typeTags', 'TagsInput', {
                'Input--outline': outline
            }, {
                'Input--error': hasError
            }, {
                'Input--disabled': disabled
            }, {
                'Input--readOnly': readOnly
            }, className, inputProps.className),
            renderLayout: this.defaultRenderLayout,
            renderInput: this.defaultRenderInput,
            renderTag: this.defaultRenderTag,
            value: value,
            onChange: this.handleOnChange,
            disabled: disabled,
            ...enhancedTagsInputProps
        });
    }
}
TagsInput.propTypes = {
    // for the sake of brevity, rest of properties are description in TextField.
    /**
	 * Whether async validation on field is in progress
	 */ asyncValidationInProgress: PropTypes.bool,
    /**
	 * Auto focus property on html input element
	 */ autoFocus: PropTypes.bool,
    /**
	 * Custom className for TagsInput component outer div wrapper
	 */ className: PropTypes.string,
    /**
	 * Custom controll className for input wrapping div element
	 */ controlClassName: PropTypes.string,
    /**
	 * Custom controll className for html input element
	 */ controlInputClassName: PropTypes.string,
    /**
	 * Input controll props provided by textFieldComposer
	 */ controlProps: PropTypes.object,
    /**
	 * Default value of html input element
	 */ defaultInputValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    /**
	 * Whether to display required asterisk at the end of label when field is required
	 */ displayRequiredClassName: PropTypes.bool,
    /**
	 * String definition of floatingLabel test
	 */ floatingLabelText: PropTypes.string,
    /**
	 * Whether input has validation error
	 */ hasError: PropTypes.bool,
    /**
	 * Flag, indicating whether cursor position should be set at the beginning of the input when value has changed
	 */ initialCursorPosition: PropTypes.oneOf([
        cursorPositions.END,
        cursorPositions.START
    ]),
    /**
	 * Custom controll className for html input element
	 */ inputControlClassName: PropTypes.string,
    /**
	 * Props for html input element provided by textFieldComposer
	 */ inputProps: PropTypes.object,
    /**
	 * Whether input element is focused
	 */ isFocused: PropTypes.bool,
    /**
	 * Function handler fired when tags has changed.
	 *
	 * @param {object} event event targeting the element.
	 * @return {array} array of Tags
	 */ onChange: PropTypes.func,
    /**
	 * Function handler fired when input wrapper has been clicked.
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} void
	 */ onInputWrapperClick: PropTypes.func,
    /**
	 * Whether to use outlined version of input
	 */ outline: PropTypes.bool,
    /**
	 * Placeholder text for html input element
	 */ placeholder: PropTypes.string,
    /**
	 * Whether html input element should be readOnly
	 */ readOnly: PropTypes.bool,
    /**
	 * Show whether Component is required
	 */ required: PropTypes.bool,
    /**
	 * Proptypes of TagsInput Component, for whole definition check https://github.com/olahol/react-tagsinput
	 */ tagsInputProps: PropTypes.object,
    /**
	 * Type of html input element
	 */ type: PropTypes.string,
    /**
	 * Value of TagsInput, is an Array of Tags
	 */ value: PropTypes.array
};
export { TagsInput };
export default o(textFieldComposer, withDefaultComponentProps(COMPONENT_NAME, {
    inputProps: {},
    controlProps: {},
    tagsInputProps: {
        addKeys: [
            13
        ]
    },
    value: [],
    onChange: noop,
    initialCursorPosition: cursorPositions.END,
    displayRequiredClassName: true
}))(TagsInput);
